import { Autocomplete, Icon, TextContainer } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import React, { useState, useCallback, useMemo, useEffect } from 'react';

export default function CustomAutocomplete({
  label,
  placeholder = 'Search',
  optionsList,
  defaultValue = '',
  onChange,
}) {
  const deselectedOptions = useMemo(() => optionsList, [optionsList]);

  const [selectedOptions, setSelectedOptions] = useState(
    defaultValue ? [defaultValue] : []
  );

  const [inputValue, setInputValue] = useState(() => {
    if (!defaultValue) return '';
    const matched = optionsList.find((opt) => opt.value === defaultValue);
    return matched ? matched.label : '';
  });

  const [options, setOptions] = useState(deselectedOptions);
  const [hasError, setHasError] = useState(false); // 🔴 Error state

  // 🔁 Sync with parent
  useEffect(() => {
    if (defaultValue) {
      setSelectedOptions([defaultValue]);

      const matched = optionsList.find((opt) => opt.value === defaultValue);
      setInputValue(matched ? matched.label : '');
    } else {
      setSelectedOptions([]);
      setInputValue('');
    }
  }, [defaultValue, optionsList]);

  // 🔁 Sync dropdown options when optionsList prop changes
  useEffect(() => {
    setOptions(deselectedOptions);
  }, [deselectedOptions]);

  // 🔎 Text update
  const updateText = useCallback(
    (value) => {
      setInputValue(value);
      setHasError(false); // Clear error while typing

      if (value === '') {
        setOptions(deselectedOptions);
        return;
      }

      const filterRegex = new RegExp(value, 'i');
      const resultOptions = deselectedOptions.filter((option) =>
        option.label.match(filterRegex)
      );
      setOptions(resultOptions);
    },
    [deselectedOptions]
  );

  // ✅ Selection update
  const updateSelection = useCallback(
    (selected) => {
      const selectedValue = selected[0];
      const matchedOption = optionsList.find(opt => opt.value === selectedValue);

      if (!matchedOption) {
        setHasError(true);
        return;
      }

      setSelectedOptions([selectedValue]);
      setInputValue(matchedOption.label);
      setHasError(false);

      if (onChange) {
        onChange([selectedValue]);
      }
    },
    [optionsList, onChange]
  );

  // ❌ Handle blur: validate if input is valid
  const handleBlur = () => {
    const matched = optionsList.find(
      (opt) => opt.label.toLowerCase() === inputValue.toLowerCase()
    );

    if (!matched) {
      setHasError(true);
    } else {
      setHasError(false);
      setSelectedOptions([matched.value]);

      if (onChange) {
        onChange([matched.value]);
      }
    }
  };

  // 🔠 Input field
  const textField = (
    <Autocomplete.TextField
      onChange={updateText}
      onBlur={handleBlur}
      label={label}
      value={inputValue}
      prefix={<Icon source={SearchIcon} tone="base" />}
      placeholder={placeholder}
      autoComplete="off"
      error={hasError ? 'Please select a valid option from the list' : false}
    />
  );

  // 🕳️ Empty result display
  const emptyState = (
    <React.Fragment>
      <Icon source={SearchIcon} />
      <div style={{ textAlign: 'center' }}>
        <TextContainer>Could not find any results</TextContainer>
      </div>
    </React.Fragment>
  );

  return (
    <div>
      <Autocomplete
        options={options}
        selected={selectedOptions}
        onSelect={updateSelection}
        textField={textField}
        emptyState={emptyState}
      />
    </div>
  );
}
